import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useLocation, useSearch } from "wouter";

import { scheduleWindowAt } from "@/app/prefetch";
import { useRealtimeTopic } from "@/realtime/RealtimeProvider";

import { ResultsSchedule } from "./ResultsSchedule";
import {
  readScheduleState,
  scheduleSearch,
  type ScheduleState,
  type ScheduleView,
} from "./scheduleState";
import { UpcomingSchedule } from "./UpcomingSchedule";

const VIEWS = ["upcoming", "results"] as const;

export default function SchedulePage() {
  const [location, navigate] = useLocation();
  const search = useSearch();
  const [now, setNow] = useState(Date.now);
  const state = readScheduleState(search, scheduleWindowAt(now));
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const id = useId();
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const path = location.split("?")[0];
  const canonical = scheduleSearch(search, state, window.location.search);
  useRealtimeTopic("world.races");

  useEffect(() => {
    if (canonical !== search.replace(/^\?/, "")) {
      navigate(`${path}?${canonical}${window.location.hash}`, {
        replace: true,
      });
    }
  }, [canonical, navigate, path, search]);

  function change(next: ScheduleState, replace = false) {
    navigate(
      `${path}?${scheduleSearch(search, next, window.location.search)}${window.location.hash}`,
      { replace },
    );
  }

  function tabKey(event: KeyboardEvent<HTMLButtonElement>, view: ScheduleView) {
    const current = VIEWS.indexOf(view);
    let next: number;
    if (event.key === "ArrowRight") next = (current + 1) % VIEWS.length;
    else if (event.key === "ArrowLeft")
      next = (current + VIEWS.length - 1) % VIEWS.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = VIEWS.length - 1;
    else return;
    event.preventDefault();
    change({ ...state, view: VIEWS[next] });
    tabs.current[next]?.focus();
  }

  return (
    <div className="page schedule-page">
      <header className="schedule-page__header">
        <div>
          <span className="schedule-eyebrow">Published racing</span>
          <h1>Schedule</h1>
        </div>
        <p>
          All times local · {Intl.DateTimeFormat().resolvedOptions().timeZone}
        </p>
      </header>
      <section className="schedule-panel" aria-label="Race schedule">
        <div
          className="schedule-tabs"
          role="tablist"
          aria-label="Schedule view"
        >
          {VIEWS.map((view, index) => (
            <button
              key={view}
              type="button"
              role="tab"
              id={`${id}-${view}-tab`}
              aria-controls={`${id}-${view}-panel`}
              aria-selected={state.view === view}
              tabIndex={state.view === view ? 0 : -1}
              ref={(element) => {
                tabs.current[index] = element;
              }}
              onClick={() => change({ ...state, view })}
              onKeyDown={(event) => tabKey(event, view)}
            >
              {view === "upcoming" ? "Upcoming" : "Results"}
            </button>
          ))}
        </div>
        {VIEWS.map((view) => (
          <div
            key={view}
            id={`${id}-${view}-panel`}
            role="tabpanel"
            aria-labelledby={`${id}-${view}-tab`}
            className="schedule-tabpanel"
            hidden={state.view !== view}
            tabIndex={0}
          >
            {state.view === view ? (
              view === "upcoming" ? (
                <UpcomingSchedule state={state} now={now} onChange={change} />
              ) : (
                <ResultsSchedule />
              )
            ) : null}
          </div>
        ))}
      </section>
    </div>
  );
}
